import {naturalSort} from '@benchling/js-utils';
import {compact} from 'underscore';

import {assert} from 'Asserts';
import {
  ProcessSchemaMapperType,
  ProcessMapperTokenType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  findAllUpstreamMappableNodeIds,
  isMappableTargetNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  ProcessFlowchartEdgeType,
  TransitionSource,
  FlowchartStaticEdgeData,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaDomainData,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource} from './TransitionSourceUtils';
import {
  getCustomMappingDataForDataFlowEdge,
  getTransitionSourceForNodeIdPair,
} from './types/transition/transitionAdapter';
import {parseFieldPairKey} from './types/transition/transitionKeyUtils';
import {Transition} from './types/transition/TransitionTypes';

interface BuildSchemaMapperConfigsFromStaticDataRunnerArgs {
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
  transitions: Record<string, Transition>;
  // Optional root node static data, which is generally only included here if
  // the root node is not part of the normal static nodes data (e.g. for
  // validation flowcharts we want to include the root node static data without
  // including the root node in the normal nodes array)
  rootNodeStaticData?: TaskSchemaDomainData;
}

export interface BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
  inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
}

interface ProcessFlowchartSchemaFieldMapperConfig {
  fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec;
  fromTaskDefinitionVersionId: string;
  fromNodeConfigClientId: string;
}

class BuildSchemaMapperConfigsFromStaticDataRunner {
  private readonly nodes: Array<StaticFlowchartNodeData>;
  private readonly edges: Array<FlowchartStaticEdgeData>;
  private readonly transitions: Record<string, Transition>;

  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private rootNode: TaskSchemaStaticNodeData | undefined;
  // Mapping of a hashkey based on PTDVs to schema mapper configs for deduplication
  private existingSchemaMapperConfigs: Map<string, ProcessFlowchartSchemaMapperConfigSpec>;

  constructor(args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs) {
    this.nodes = args.nodes;
    this.edges = args.edges;
    this.transitions = args.transitions;

    this.inboundSchemaMapperConfigsByNodeId = {};
    this.rootNode =
      this.nodes.find(
        (node): node is TaskSchemaStaticNodeData =>
          isStaticTaskSchemaNodeData(node) &&
          node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) ?? args.rootNodeStaticData;
    this.existingSchemaMapperConfigs = new Map();
  }

  /**
   * Creates a unique key for schema mapper config deduplication based on task definition versions.
   * For merging transitions, the key includes all sorted source PTDV IDs to uniquely identify
   * the transition.
   */
  private getSchemaMapperConfigKey(
    fromTaskDefinitionVersionIds: Array<string>,
    toTaskDefinitionVersionId: string,
    mapsToType: ProcessMapperTokenType
  ): string {
    const sortedSources = naturalSort([...fromTaskDefinitionVersionIds]);
    return `${sortedSources.join('+')}-${toTaskDefinitionVersionId}-${mapsToType}`;
  }

  private getSchemaMapperClientId(
    sourcePTDVIds: Array<string>,
    targetNodeId: string,
    mapsToType: ProcessMapperTokenType
  ): string {
    const sortedSourceIds = naturalSort([...sourcePTDVIds]);
    return `schema-mapper-${sortedSourceIds.join('+')}-${targetNodeId}-${mapsToType}`;
  }

  private getMapsToType(
    nodeDetailsType: ProcessFlowchartNodeDetailsType | undefined
  ): ProcessMapperTokenType {
    return nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;
  }

  private createAndStoreSchemaMapperConfig(params: {
    sourcePTDVIds: Array<string>;
    targetPTDVId: string;
    targetNodeId: string;
    mapsToType: ProcessMapperTokenType;
    schemaFieldMapperConfigs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec>;
    schemaMapperSourceConfigs: Array<{
      mapsFromType: ProcessMapperTokenType;
      fromProcessTaskDefinitionVersionClientId: string;
    }>;
  }): ProcessFlowchartSchemaMapperConfigSpec {
    const clientId = this.getSchemaMapperClientId(
      params.sourcePTDVIds,
      params.targetNodeId,
      params.mapsToType
    );
    const config: ProcessFlowchartSchemaMapperConfigSpec = {
      clientId,
      toProcessTaskDefinitionVersionClientId: params.targetPTDVId,
      mapsToType: params.mapsToType,
      schemaFieldMapperConfigs: params.schemaFieldMapperConfigs,
      mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
      schemaMapperSourceConfigs: params.schemaMapperSourceConfigs,
    };
    const schemaMapperKey = this.getSchemaMapperConfigKey(
      params.sourcePTDVIds,
      params.targetPTDVId,
      params.mapsToType
    );
    this.existingSchemaMapperConfigs.set(schemaMapperKey, config);
    return config;
  }

  run(): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
    const schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec> = [];
    for (const node of this.nodes) {
      if (isMappableTargetNode({id: node.id, data: node})) {
        assert(isStaticTaskSchemaNodeData(node), 'Only task schema type nodes are expected to be mapped to');
        schemaMapperConfigSpecs.push(...this.buildSchemaMapperConfigsForTargetNode(node));
      }
    }

    // Build schema mapper configs for transitions not covered by edge-based discovery
    // (e.g. optional transitions with no control flow edges).
    // These go in the top-level schemaMapperConfigs but NOT in inboundSchemaMappers,
    // matching the old editor's pattern.
    for (const transition of Object.values(this.transitions)) {
      if (transition.isPlanned) {
        continue;
      }
      const spec = this.buildSchemaMapperConfigForOptionalTransition(transition);
      if (spec) {
        schemaMapperConfigSpecs.push(spec);
      }
    }

    return {
      schemaMapperConfigSpecs,
      inboundSchemaMapperConfigsByNodeId: this.inboundSchemaMapperConfigsByNodeId,
    };
  }

  /**
   * Builds schema mapper configs for a target node, supporting merging transitions.
   */
  private buildSchemaMapperConfigsForTargetNode(
    targetNode: TaskSchemaStaticNodeData
  ): Array<ProcessFlowchartSchemaMapperConfigSpec> {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(
      {nodes: this.nodes.map((node) => ({id: node.id, data: node})), edges: this.edges},
      targetNode.id
    );
    if (this.inboundSchemaMapperConfigsByNodeId[targetNode.id] == null) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id] = [];
    }

    const upstreamMappableNodes = this.nodes.filter(
      (node): node is TaskSchemaStaticNodeData =>
        upstreamMappableNodeIds.includes(node.id) && isStaticTaskSchemaNodeData(node)
    );

    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    // Group source nodes by their transition to the target.
    // This handles merging transitions (multiple sources to same target node).
    const sourceNodeGroups = this.groupSourceNodesByTransition(upstreamMappableNodes, targetPTDVId);

    return compact(
      sourceNodeGroups.map((sourceNodes) =>
        this.buildSchemaMapperConfigForTransition(sourceNodes, targetNode)
      )
    );
  }

  /**
   * Groups upstream source nodes by their transition to the target.
   * Nodes that share a merging transition (multiple sources to same target) are grouped together.
   * When multiple nodes share the same PTDV within a transition, they are split into separate
   * groups so that the deduplication in buildSchemaMapperConfigForTransition produces one config
   * with separate inbound entries per node.
   *
   * Example: If nodes A1 and A2 both use PTDV "X" and both transition to B, they are split into
   * [A1] and [A2] so each gets its own schema mapper config referencing the same PTDV.
   */
  private groupSourceNodesByTransition(
    sourceNodes: Array<TaskSchemaStaticNodeData>,
    targetPTDVId: string
  ): Array<Array<TaskSchemaStaticNodeData>> {
    const transitionToNodes = new Map<string, Array<TaskSchemaStaticNodeData>>();
    const nodesWithoutTransition: Array<TaskSchemaStaticNodeData> = [];

    for (const sourceNode of sourceNodes) {
      const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
      const matchingTransition = this.findTransitionForSourceTarget(sourcePTDVId, targetPTDVId);

      if (matchingTransition) {
        const existing = transitionToNodes.get(matchingTransition.id) ?? [];
        existing.push(sourceNode);
        transitionToNodes.set(matchingTransition.id, existing);
      } else {
        nodesWithoutTransition.push(sourceNode);
      }
    }

    const groups: Array<Array<TaskSchemaStaticNodeData>> = [];
    for (const transitionNodes of transitionToNodes.values()) {
      groups.push(...this.splitGroupByDuplicatePTDV(transitionNodes));
    }

    for (const node of nodesWithoutTransition) {
      groups.push([node]);
    }

    return groups;
  }

  /**
   * Splits a node group so that each unique PTDV appears only once in the primary group.
   * Nodes with duplicate PTDVs become their own groups, allowing the existing dedup logic
   * in buildSchemaMapperConfigForTransition to create one config with multiple inbound entries.
   */
  private splitGroupByDuplicatePTDV(
    nodes: Array<TaskSchemaStaticNodeData>
  ): Array<Array<TaskSchemaStaticNodeData>> {
    const seenPTDVs = new Set<string>();
    const primaryGroup: Array<TaskSchemaStaticNodeData> = [];
    const duplicateGroups: Array<Array<TaskSchemaStaticNodeData>> = [];

    for (const node of nodes) {
      const ptdvId = node.taskDefinitionVersionSpec.clientId;
      if (seenPTDVs.has(ptdvId)) {
        duplicateGroups.push([node]);
      } else {
        seenPTDVs.add(ptdvId);
        primaryGroup.push(node);
      }
    }

    return primaryGroup.length > 0 ? [primaryGroup, ...duplicateGroups] : duplicateGroups;
  }

  /**
   * Builds a schema mapper config for an optional transition (no control flow edges).
   * The resulting spec goes into the top-level schemaMapperConfigs but NOT into inboundSchemaMappers.
   */
  private buildSchemaMapperConfigForOptionalTransition(
    transition: Transition
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    const targetNode = this.nodes.find(
      (n): n is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === transition.targetPTDVId
    );

    const mapsToType = this.getMapsToType(targetNode?.nodeDetailsType);
    const schemaMapperKey = this.getSchemaMapperConfigKey(
      transition.sourcePTDVIds,
      transition.targetPTDVId,
      mapsToType
    );

    if (this.existingSchemaMapperConfigs.has(schemaMapperKey)) {
      return null;
    }

    const schemaMapperSourceConfigs = transition.sourcePTDVIds.map((sourcePTDVId) => {
      const transitionSource =
        transition.transitionSourcesBySourcePTDVId[sourcePTDVId] ?? TransitionSource.OUTPUTS;
      const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);
      return {
        mapsFromType,
        fromProcessTaskDefinitionVersionClientId: sourcePTDVId,
      };
    });

    return this.createAndStoreSchemaMapperConfig({
      sourcePTDVIds: transition.sourcePTDVIds,
      targetPTDVId: transition.targetPTDVId,
      targetNodeId: targetNode?.id ?? transition.targetPTDVId,
      mapsToType,
      schemaFieldMapperConfigs: this.buildSchemaFieldMapperConfigsForOptionalTransition(transition),
      schemaMapperSourceConfigs,
    });
  }

  private buildSchemaFieldMapperConfigsForOptionalTransition(
    transition: Transition
  ): Array<ProcessFlowchartSchemaFieldMapperConfigSpec> {
    const configs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec> = [];

    for (const [fieldPairKey, customMappingData] of Object.entries(transition.customMappingDataByFieldPair)) {
      const parsed = parseFieldPairKey(fieldPairKey);
      if (parsed == null) {
        continue;
      }

      const transitionSource =
        transition.transitionSourcesBySourcePTDVId[parsed.sourcePTDVId] ?? TransitionSource.OUTPUTS;
      const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

      const lookupConfig =
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.buildDefaultLookupConfig(
              parsed.sourceSectionType,
              transitionSource,
              parsed.sourceFieldClientId,
              parsed.sourcePTDVId
            );

      configs.push({
        fromProcessTaskDefinitionVersionClientId: parsed.sourcePTDVId,
        mapsFromType,
        toProcessFieldClientIdentifier: parsed.targetFieldClientId,
        resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
        lookupConfig,
      });
    }

    return configs;
  }

  /**
   * Finds a transition that includes the given source PTDV and has the given target PTDV.
   * Note that this does not account for if multiple transitions share a source PTDV and target PTDV.
   * We have a validation rule against this.
   */
  private findTransitionForSourceTarget(sourcePTDVId: string, targetPTDVId: string): Transition | null {
    for (const transition of Object.values(this.transitions)) {
      if (transition.targetPTDVId === targetPTDVId && transition.sourcePTDVIds.includes(sourcePTDVId)) {
        return transition;
      }
    }
    return null;
  }

  /**
   * Builds a schema mapper config for a transition (supporting multiple sources for merging transitions).
   * @param sourceNodes - Array of source nodes
   * @param targetNode - The target node
   * @returns A schema mapper config spec, or null if one already exists for this transition
   */
  private buildSchemaMapperConfigForTransition(
    sourceNodes: Array<TaskSchemaStaticNodeData>,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    if (sourceNodes.length === 0) {
      return null;
    }

    const mapsToType = this.getMapsToType(targetNode.nodeDetailsType);
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const firstSourcePTDVId = sourceNodes[0].taskDefinitionVersionSpec.clientId;
    const matchingTransition = this.findTransitionForSourceTarget(firstSourcePTDVId, targetPTDVId);
    const sourcePTDVIds = matchingTransition
      ? [...new Set(matchingTransition.sourcePTDVIds)]
      : [...new Set(sourceNodes.map((node) => node.taskDefinitionVersionSpec.clientId))];
    const schemaMapperKey = this.getSchemaMapperConfigKey(sourcePTDVIds, targetPTDVId, mapsToType);

    const existingSchemaMapperConfig = this.existingSchemaMapperConfigs.get(schemaMapperKey);

    if (existingSchemaMapperConfig) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
        toNodeConfigClientId: targetNode.id,
        schemaMapperConfigClientId: existingSchemaMapperConfig.clientId,
        schemaMapperDependencies: sourceNodes.map((node) => ({fromNodeConfigClientId: node.id})),
      });

      return null;
    }

    const allSchemaFieldMapperConfigs: Array<ProcessFlowchartSchemaFieldMapperConfig> = [];
    for (const sourceNode of sourceNodes) {
      const fieldMapperConfigs = this.buildSchemaFieldMapperConfigsNodePair(sourceNode, targetNode);
      allSchemaFieldMapperConfigs.push(...fieldMapperConfigs);
    }

    // Deduplicate source PTDVs to avoid duplicate schema mapper source configs
    const seenSourcePTDVIds = new Set<string>();
    const schemaMapperSourceConfigs = compact(
      sourceNodes.map((sourceNode) => {
        const ptdvId = sourceNode.taskDefinitionVersionSpec.clientId;
        if (seenSourcePTDVIds.has(ptdvId)) {
          return null;
        }
        seenSourcePTDVIds.add(ptdvId);
        const mapsFromType = this.getMapperTokenTypeFromTransitionSource(
          this.getTransitionSourceForNode(sourceNode, targetNode)
        );
        return {
          mapsFromType,
          fromProcessTaskDefinitionVersionClientId: ptdvId,
        };
      })
    );

    const config = this.createAndStoreSchemaMapperConfig({
      sourcePTDVIds,
      targetPTDVId: targetNode.taskDefinitionVersionSpec.clientId,
      targetNodeId: targetNode.id,
      mapsToType,
      schemaFieldMapperConfigs: allSchemaFieldMapperConfigs.map(({fieldMapperConfig}) => fieldMapperConfig),
      schemaMapperSourceConfigs,
    });

    this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
      toNodeConfigClientId: targetNode.id,
      schemaMapperConfigClientId: config.clientId,
      schemaMapperDependencies: sourceNodes.map((node) => ({fromNodeConfigClientId: node.id})),
    });

    return config;
  }

  private getTransitionSourceForNode(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): TransitionSource {
    const taskSchemaNodes = this.nodes.filter((node) => isStaticTaskSchemaNodeData(node));

    const transitionSource =
      getTransitionSourceForNodeIdPair(sourceNode.id, targetNode.id, this.transitions, this.nodes) ??
      getDefaultTransitionSource(sourceNode.id, taskSchemaNodes);
    return transitionSource;
  }

  /**
   * Gets schema field mapper configs for a pair of nodes.
   */
  private buildSchemaFieldMapperConfigsNodePair(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): Array<ProcessFlowchartSchemaFieldMapperConfig> {
    // Find data flow edges that match the same source and target as this control flow edge
    const matchingDataFlowEdges = this.edges.filter(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.source === sourceNode.id &&
        edge.target === targetNode.id
    );

    const newConfigs: Array<ProcessFlowchartSchemaFieldMapperConfig> = [];

    for (const edge of matchingDataFlowEdges) {
      const schemaFieldMapperConfig = this.buildSchemaFieldMapperConfig(edge, sourceNode, targetNode);
      const config = {
        fieldMapperConfig: schemaFieldMapperConfig,
        fromTaskDefinitionVersionId: sourceNode.taskDefinitionVersionSpec.clientId,
        fromNodeConfigClientId: sourceNode.id,
      };
      newConfigs.push(config);
    }

    return newConfigs;
  }

  private buildSchemaFieldMapperConfig(
    dataFlowEdge: FlowchartStaticEdgeData,
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;
    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;

    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(fromFieldHandle);

    const toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(toFieldHandle).fieldClientId;
    const customMappingData = getCustomMappingDataForDataFlowEdge(dataFlowEdge, this.transitions, this.nodes);

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

    return {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(
              fromFieldClientInfo.sectionType,
              sourceNode,
              targetNode,
              fromFieldClientInfo.fieldClientId
            ),
    };
  }

  /**
   * If the field mapper config is a duplicate of a previously generated field mapper config, returns the client ID of that field mapper config.
   * Otherwise, returns the new field mapper config.
   */
  buildFieldMapperConfigFromDataFlowEdge(
    clientId: string,
    dataFlowEdge: FlowchartStaticEdgeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const sourceNode = this.nodes.find((node) => node.id === dataFlowEdge.source);
    assert(sourceNode != null, 'Expected to find source node', {edge: dataFlowEdge});
    assert(
      isStaticTaskSchemaNodeData(sourceNode),
      'Only task schema type nodes are expected to be mapped from'
    );
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;

    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;

    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(fromFieldHandle);

    const toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(toFieldHandle).fieldClientId;
    const customMappingData = getCustomMappingDataForDataFlowEdge(dataFlowEdge, this.transitions, this.nodes);

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

    const fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec = {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(
              fromFieldClientInfo.sectionType,
              sourceNode,
              targetNode,
              fromFieldClientInfo.fieldClientId
            ),
    };

    const config = {
      fieldMapperConfig,
      fromTaskDefinitionVersionId,
      fromNodeConfigClientId: sourceNode.id,
    };

    return config.fieldMapperConfig;
  }

  private getLookupConfigFromFieldAndTransitionSource(
    sectionType: TaskSchemaNodeSectionType,
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData,
    fromFieldClientId: string
  ): LookupConfigSpec {
    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    return this.buildDefaultLookupConfig(
      sectionType,
      transitionSource,
      fromFieldClientId,
      sourceNode.taskDefinitionVersionSpec.clientId
    );
  }

  private buildDefaultLookupConfig(
    sectionType: TaskSchemaNodeSectionType,
    transitionSource: TransitionSource,
    fromFieldClientId: string,
    sourcePTDVId: string
  ): LookupConfigSpec {
    const lookupSteps: Array<LookupStepSpec> = [];
    if (sectionType === TaskSchemaNodeSectionType.INPUTS && transitionSource === TransitionSource.OUTPUTS) {
      // If the transition source is the node output but the field is coming from the node input, we need to add
      // a lookup step to go from the output to the inputs (aka from the process output to the process task).
      lookupSteps.push({type: LookupStepType.PROCESS_TASK});
    } else if (sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      // If the field is in the map from start section we first do a flowchart task lookup
      assert(this.rootNode != null, 'Start node must be present to map from root');
      lookupSteps.push({
        type: LookupStepType.FLOWCHART_PROCESS_TASK,
        processTaskSchema: this.rootNode.taskSchemaId,
      });
      lookupSteps.push({
        type: LookupStepType.SCHEMA_FIELD,
        schemaField: fromFieldClientId,
      });
      return {
        isMulti: false,
        lookupSteps,
      };
    }
    lookupSteps.push({
      type: LookupStepType.PROCESS_TASK_DEFINITION_FIELD,
      processTaskDefinitionFieldIdentifier: fromFieldClientId,
      processTaskDefinitionVersion: sourcePTDVId,
    });
    return {
      isMulti: false,
      lookupSteps,
    };
  }

  private getMapperTokenTypeFromTransitionSource(transitionSource: TransitionSource): ProcessMapperTokenType {
    return transitionSource === TransitionSource.OUTPUTS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;
  }
}

/**
 * Builds the default field mapper config for a data flow edge.
 *
 * This is used to differentiate between a "field-to-field" mapping configured by drawing an edge between two
 * fields and a "custom mapping" configured by a custom lookup config.
 */
export function buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
  clientId,
  dataFlowEdge,
  nodes,
  transitions,
}: {
  clientId: string;
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}): ProcessFlowchartSchemaFieldMapperConfigSpec {
  const targetNode = nodes.find((node) => node.id === dataFlowEdge.target);
  assert(targetNode != null && isStaticTaskSchemaNodeData(targetNode), 'Expected to find target node', {
    edge: dataFlowEdge,
  });

  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner({
    nodes,
    edges: [dataFlowEdge],
    transitions,
  });
  return runner.buildFieldMapperConfigFromDataFlowEdge(clientId, dataFlowEdge, targetNode);
}

export default function buildSchemaMapperConfigsFromStaticData(
  args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs
): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner(args);
  return runner.run();
}
